<template>
  <VDataGridV2
    id="demands-registry"
    entity-type="demand"
    paginated
    sticky
    :loader="loader"
    selection-control="checkbox"
    :context-menu="$core.currentPartner.isBuyer === true ? DemandsService.contextMenu : null"
    v-model:selected-rows="selectedDemands"
    v-model:selectable="selectable"
    @row:dblclick="openCard"
  >
    <template #empty>
      Нет потребностей
    </template>

    <VDataGridColumnV2
      id="comment"
      content-class="comment"
      v-if="$core.currentPartner.isBuyer === true"
    >
      <template #default="{ row }">
        <CommentsButton entity-type-name="demandV1" :entity="row" />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="id"
      label="№"
      content-align="left"
      width="60px"
      content-class="ellipsis"
    >
      <template #default="{ row }">
        <VLink @click="openCard(row)">{{ row.id }}</VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isSeller === true"
      id="buyer"
      width="220px"
      content-align="left"
      content-class="ellipsis"
    >
      <template #label>
        Грузополучатель
        <div class="description">
          Юр. лицо
        </div>
      </template>
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.buyer?.name"
        >
          {{ row.buyer?.name || '--' }}
        </div>
        <div
          class="description ellipsis"
          :title="row.buyerCompany?.name"
        >
          {{ row.buyerCompany?.name || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="code"
      content-align="center"
      label="Арт"
      width="75px"
      content-class="ellipsis"
    >
      <template #default="{ row }">
        {{ row.product.code }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="name"
      width="350px"
      content-class="ellipsis"
      filterable
      filter-send-name="pattern"
      :filter-props="{ placeholder: 'Введите наименование товара или артикул', clearable: true }"
    >
      <template #label>
        Сырьё
        <div class="description">
          Производители
        </div>
      </template>
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.productName"
        >
          {{ row.productName }}
        </div>

        <div
          v-if="row.manufacturersStringRepresentation !== null"
          class="description ellipsis"
          :title="'Производители: ' + row.manufacturersStringRepresentation"
        >
          {{ row.manufacturersStringRepresentation }}
        </div>
        <VBadges>
          <VBadge
            v-if="row.isPreorderNeeded === true"
          >
            Предзаказ
          </VBadge>

          <VBadge v-if="$core.currentPartner.isSeller && row.preorderMonthlyReserve.isEmpty() === false">
            Страховой запас от {{ row.preorderMonthlyReserve.format(row.unit) }}
          </VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isSeller"
      id="descriptionForSeller"
      label="Описание"
      width="350px"
    >
      <template #default="{ row }">
        {{ row.product?.descriptionForSeller || '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="desiredReceiptPeriod"
      width="180px"
      content-align="left"
      header-class="nowrap"
      label="Период поставки"
      filterable
      filter-type="date-range"
      filter-send-name="desiredReceiptPeriod"
      :filter-props="{ placeholder: 'Введите период поставки', clearable: true }"
    >
      <template #default="{ row }">
        <div
          :class="row.deliveryDateClass"
          :title="row.deliveryDateTooltip"
        >
          <template v-if="row.isPreorderNeeded">
            {{ row.preorderShipmentPeriod.toString({ prefix: 'с', middle: 'по' }) }}
          </template>
          <template v-else>
            к {{ row.desiredReceiptDate?.format() }}
          </template>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qty"
      :label="$core.currentPartner.isBuyer === true ? 'Количество' : 'Кол-во'"
      :width="$core.currentPartner.isBuyer === true ? '160px' : '90px'"
      content-align="right"
    >
      <template #label v-if="$core.currentPartner.isBuyer === true"
      >
        Осталось
        <div class="description">
          Всего
        </div>
      </template>
      <template #default="{ row }">
        <template v-if="$core.currentPartner.isBuyer === true">
          {{ row.qtyLeft?.format(row.unit) }}
          <div class="description">
            {{ row.qtyDesired?.format(row.unit) }}
          </div>
        </template>
        <template v-else>
          {{ row.qtyDesired?.format(row.unit) }}
        </template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isBuyer === true"
      id="hasTender"
      label="Тендер"
      width="90px"
      content-align="center"
      filterable
      filter-type="select"
      filter-send-name="hasTender"
      :filter-props="{ clearable: true, options: [{ id: true, name: 'Есть' }, { id: false, name: 'Нет' }] }"
    >
      <template #default="{ row }">
        {{ row.hasTender ? "Есть" : "Нет" }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isBuyer === true"
      id="responsible"
      width="140px"
      label="Ответственный"
      filterable
      filter-type="select"
      filter-send-name="responsible"
      :filter-props="{
        clearable: true,
        placeholder: 'Введите ФИО',
        searchFunction: (pattern) => PersonsService.searchPersons(pattern, $core.currentPartner?.id)
      }"
      :filter-default-value="$core.currentUser"
      content-align="center"
      content-class="ellipsis"
    >
      <template #default="{ row }">
        {{ row.responsibleBuyer ? row.responsibleBuyer.name : "--" }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="$core.currentPartner.isSeller === true"
    >
      <template #default="{ row }">

      <VButton @click="onCreateOfferButtonClicked(row)"> Создать предложение </VButton>
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2 v-if="$core.currentPartner.isBuyer === true">
      <template #default="{ selectedRows }">
        <div class="left-buttons">
          <VButton
            :theme="selectable ? 'link' : 'default'"
            @click="toggleSelectableMode"
          >
            {{ selectable ? 'Отменить выбор' : 'Выбрать' }}
          </VButton>
          <VButton
            v-if="selectable === false"
            @click="selectAllFiltered"
          >
            Выбрать все
          </VButton>
        </div>

        <VButton
          v-if="selectable"
          @click="onRemoveSelected"
        >
          Удалить потребности
        </VButton>

        <template v-if="selectable">
          Выбрано: {{ selectedRows.length }}
          {{ $core.utils.regional.ru.pluralize(selectedRows.length, 'строка', 'строки', 'строк') }}

          <VButton
            v-if="hasSelectedWithTender"
            @click="removeHasTenderDemands"
          >
            Вычесть потребности с тендерами
          </VButton>

          <div v-if="hasSelectedWithTender">
            <div>
              <VIcon material="priority_high"/>
              <div>
                {{ createTenderDisabledError }}
              </div>
            </div>
          </div>

          <VButton
            theme="primary"
            :disabled="selectedRows.length === 0 || createTenderDisabledError !== null"
            @click="createTenders"
          >
            Создать тендеры
          </VButton>
        </template>
        <template v-else>
          <VButton @click="DemandsService.showOrderCreateDialog(false, refresh)">Создать потребности на заказ</VButton>
          <VButton @click="DemandsService.showOrderCreateDialog(true, refresh)">Создать потребности на предзаказ</VButton>
        </template>
      </template>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">

import Demand         from '@/domains/Demand/Demand';
import DemandsService from '@/domains/Demand/DemandsService';
import PersonsService from '@/domains/Persons/PersonsService';
import DataGridLoader from '@/lib/components/VDataGridV2/DataGridLoader';
import OffersService  from "@/domains/Offers/OffersService";

export default defineComponent({
  name: 'DemandsRegistry',

  props: {
    params: {
      type:    Object as PropType<Parameters<typeof DemandsService.getListPaginated>[0]>,
      default: () => ({}),
    },
  },

  expose: ['refresh'],

  setup() {
   return {
      OffersService,
      DemandsService,
      PersonsService
   }
  },

  data() {
    return {
      selectable:      false,
      selectedDemands: [] as Demand[],

      loader: new DataGridLoader(DemandsService.getListPaginated)
        .useReactiveSource(() => ({
          ...this.params,
          showAllPartners: this.$core.currentPartner.isSeller,
          requireCommentsInfo: true,
        })),
    };
  },

  computed: {
    /**
     * Среди выбранных есть потребность с уже созданным тендером
     */
    hasSelectedWithTender(): boolean {
      return this.selectedDemands.some((_demand: Demand) => _demand.hasTender);
    },

    /**
     * Текст ошибки, блокирующей создание тендера; null если можно создавать
     */
    createTenderDisabledError(): string | null {
      if (this.hasSelectedWithTender) {
        return 'На некоторые потребности уже был создан тендер';
      }
      else {
        return null;
      }
    },
  },

  methods: {
    async openCard(demand?: Demand): Promise<void> {
      if (this.selectable) return;
      if (await DemandsService.showDemandCard(demand ?? null)) {
        this.loader.refresh();
      }
    },

    /**
     * Обновляет данные грида
     */
    refresh(): void {
      this.loader.refresh();
    },

    /**
     * Переключает режим множественного выделения; при выходе очищает выбор
     */
    toggleSelectableMode(): void {
      this.selectable = !this.selectable;
      if (this.selectable === false) {
        this.selectedDemands = [];
      }
    },

    /**
     * Выбирает все потребности по текущим фильтрам (одним запросом без пагинации)
     */
    async selectAllFiltered(): Promise<void> {
      this.toggleSelectableMode();

      const total = this.loader.pagination?.total ?? 0;
      if (total === 0) return;

      const { data } = await DemandsService.getListPaginated({
        ...this.loader.filters,
        pagination: { limit: total },
      });
      this.selectedDemands = data;
    },

    /**
     * Убирает из выбора потребности, у которых уже есть тендер
     */
    removeHasTenderDemands(): void {
      this.selectedDemands = this.selectedDemands.filter(demand => demand.hasTender === false);
    },

    /**
     * Создаёт тендеры по выбранным потребностям через сервис тендеров
     */
    async createTenders(): void {
      await this.$core.dialogs.TenderCreateCard.showAsPromise({
        demands: this.selectedDemands
      });

      this.toggleSelectableMode();
      this.refresh();
    },

    /**
     * Массовое удаление выбранных потребностей (подтверждение и сводные нотификации — в сервисе)
     */
    async onRemoveSelected(): Promise<void> {
      if (this.selectable === false) return;
      if (this.$core.utils.isNotEmptyArray(this.selectedDemands) === false) return;

      const { success } = await DemandsService.deleteMany(this.selectedDemands);

      if (success.length > 0) {
        this.toggleSelectableMode();
        this.refresh();
      }
    },

    async onCreateOfferButtonClicked(demand) {
      // в реестре потребности без характеристик, надо полностью заполучить запись
      const _demand = await DemandsService.get(demand.id)
      await OffersService.showOfferEditDialog({demand: _demand})
    }

  },
});
</script>
